import { reactive, ref, computed, onMounted, onBeforeUnmount } from 'vue'
import globals from '@/globals.js'

/**
 * Utilities that don't depend on Vue instance
 */
const regex = {
  url: /^(?<protocol>(?:http|https|ftp|news|.*?):\/\/)?(?<url>.*)$/,
  mail: /^mailto\:(?<email>.*?)\??(?:subject=(?<subject>.*?))?(?:\&amp\;|\&)(?:body=(?<body>.*?))?$/,
  tel: /^tel:(?<phone>.*?)$/,
  anchor: /^(?<anchor>#.*)$/,
}

// --- Link helpers ------------------------------------------------------------
const toMailto = (e) =>
  `mailto:${e.email}?subject=${e.subject || ''}&amp;body=${e.body || ''}`

const toTel = (p) => `tel:${p}`

/** Normalize a raw URL against an explicit protocol (or keep inline protocol). */
const normalizeUrl = (protocol, raw) => {
  if (!raw) return ''
  const m = raw.match(regex.url)
  let finalProtocol = protocol
  let path = raw
  if (m) {
    if (m?.groups?.protocol) finalProtocol = m.groups.protocol
    if (m?.groups?.url) path = m.groups.url
  }
  return (finalProtocol || '') + path
}

/** Build the final href based on the current openedLink state. */
const buildHrefFromState = (openedLink) => {
  switch (openedLink.type) {
    case 'Email':
      return openedLink.email?.email ? toMailto(openedLink.email) : ''
    case 'Telephone':
      return openedLink.phone ? toTel(openedLink.phone) : ''
    case 'Anchor':
    case 'URL':
    default:
      return openedLink.protocol === ''
        ? (openedLink.href || '')
        : normalizeUrl(openedLink.protocol, openedLink.href)
  }
}


const __ = (s) => globals.I.gt.__(s)
const __x = (s, params) => globals.I.gt.__x(s, params)

function buildLinkTargets(openedLink) {
  return [
    { label: '_blank', value: '_blank', method: () => (openedLink.attr.target = '_blank') },
    { label: '_self', value: '_self', method: () => (openedLink.attr.target = '_self') },
    { label: '_parent', value: '_parent', method: () => (openedLink.attr.target = '_parent') },
    { label: '_top', value: '_top', method: () => (openedLink.attr.target = '_top') },
    { label: __('<other>'), value: '', method: () => (openedLink.attr.target = '') },
  ]
}

function buildLinkTypes(openedLink) {
  return [
    { label: __('URL'), value: 'URL', method: () => { openedLink.type = 'URL'; openedLink.attr.target = '_blank' } },
    { label: __('In page anchor'), value: 'Anchor', method: () => { openedLink.type = 'Anchor'; openedLink.protocol = ''; openedLink.attr.target = '_self' } },
    { label: __('Email'), value: 'Email', method: () => { openedLink.type = 'Email' } },
    { label: __('Telephone'), value: 'Telephone', method: () => { openedLink.type = 'Telephone' } },
  ]
}

// Read the protocols from the live Link (or MAMLink) extension
function readLinkProtocols(editor, extName = 'link') {
  if (!editor) return null

  const em = editor.extensionManager
  const ext =
    (em && Array.isArray(em.extensions) && em.extensions.find(e => e && e.name === extName)) ||
    (em && em.extensionsByName && em.extensionsByName[extName])

  return (ext && ext.options && ext.options.protocols) || null
}

// Turn the extension's protocols into dropdown items
function buildProtocols(openedLink, linkProtocols) {
  const normalize = (p) => {
    const scheme = typeof p === 'string' ? p : (p && p.scheme)
    if (!scheme) return null

    // If optionalSlashes is true (e.g. mailto), use ":"; otherwise "://"
    const wantsColon = (p && p.optionalSlashes) || scheme === 'mailto'
    const suffix = wantsColon ? ':' : '://'
    const value = scheme + suffix

    return {
      label: value,
      value,
      method: () => { openedLink.protocol = value },
    }
  }

  if (Array.isArray(linkProtocols) && linkProtocols.length) {
    const items = linkProtocols
      .map(normalize)
      .filter(Boolean)

    // de-duplicate by value just in case
    const seen = new Set()
    const unique = items.filter(item => {
      if (seen.has(item.value)) return false
      seen.add(item.value)
      return true
    })

    unique.unshift({ label: '<other>', value: '', method: () => (openedLink.protocol = '') })
    return unique
  }

  // Fallback if extension has no protocols configured
  return [
    { label: '<other>', value: '', method: () => (openedLink.protocol = '') },
    { label: 'http://', value: 'http://', method: () => (openedLink.protocol = 'http://') },
    { label: 'https://', value: 'https://', method: () => (openedLink.protocol = 'https://') },
    { label: 'ftp://', value: 'ftp://', method: () => (openedLink.protocol = 'ftp://') },
    { label: 'news://', value: 'news://', method: () => (openedLink.protocol = 'news://') },
    { label: 'mailto:', value: 'mailto:', method: () => (openedLink.protocol = 'mailto:') },
  ]
}

export function useMamTiptap(editor) {
  // State
  const DEFAULT_IMAGE_WIDTH = 300

  const openedImage = reactive({
    attr: {},
    node: null,
    isNew: true,
    width: null,
    height: null,
  })

  const openedLink = reactive({
    attr: { target: '_blank' },
    isNew: true,
    type: '',
    email: {},
    href: '',
    protocol: '',
    text: null,
    setUp: false,
    phone: '',
  })

  // Options (dynamic so their methods can mutate reactive `openedLink`)
  const linkTargets = ref(buildLinkTargets(openedLink))
  const linkTypes = ref(buildLinkTypes(openedLink))
  const protocols = ref(buildProtocols(openedLink, readLinkProtocols(editor?.value)))

  // Helpers
  function activeLabel(currentValue, options) {
    return options.find((item) => item.value === currentValue)
  }

  function resetOpenedImage() {
    openedImage.attr = {}
    openedImage.width = null
    openedImage.height = null
    openedImage.isNew = true
    openedImage.node = null
  }

  function resetOpenedLink() {
    openedLink.attr = { target: '_blank' }
    openedLink.email = {}
    openedLink.phone = ''
    openedLink.href = ''
    openedLink.protocol = ''
    openedLink.text = null
    openedLink.setUp = false
    openedLink.isNew = true
    openedLink.type = ''
  }

  function setOpenedLinkProps(attrs) {
    let matched
    let protocol = openedLink.protocol
    openedLink.type = 'URL'

    if (attrs?.href) {
      openedLink.href = attrs.href
      if ((matched = attrs.href.match(regex.mail))) {
        openedLink.type = 'Email'
        openedLink.email = { ...matched.groups }
      } else if ((matched = attrs.href.match(regex.anchor))) {
        openedLink.type = 'Anchor'
        openedLink.href = matched.groups.anchor
      } else if ((matched = attrs.href.match(regex.tel))) {
        openedLink.type = 'Telephone'
        openedLink.phone = matched.groups.phone
      } else if ((matched = attrs.href.match(regex.url))) {
        if (matched.groups.protocol) {
          protocol = matched.groups.protocol
        }
        openedLink.href = matched.groups.url
        if (!activeLabel(protocol, protocols.value)) {
          protocol = ''
          openedLink.href = attrs.href
        }
      }
    }
    openedLink.protocol = protocol
  }

  function getEditorImage() {
    const ed = editor.value
    if (!ed) return null

    const { state } = ed
    const { selection } = state
    const { $from } = selection

    const imageType  = state.schema.nodes.image
    const figureType = state.schema.nodes.figure

    const isType = (n, type) => !!n && !!type && n.type === type
    const isImageOrFigure = (n) => isType(n, imageType) || isType(n, figureType)

    // 1) Direct NodeSelection on image/figure
    let node = null
    if (selection.node && isImageOrFigure(selection.node)) {
      node = selection.node
    }

    // 2) Immediate neighbors at the cursor
    if (!node && isImageOrFigure($from.nodeBefore)) node = $from.nodeBefore
    if (!node && isImageOrFigure($from.nodeAfter))  node = $from.nodeAfter

    // 3) Walk up ancestors to catch figure when cursor is in caption
    if (!node) {
      for (let d = $from.depth; d >= 0; d--) {
        const n = $from.node(d)
        if (isImageOrFigure(n)) { node = n; break }
      }
    }

    // Populate openedImage (no selection changes!)
    resetOpenedImage()
    openedImage.node  = node || null
    openedImage.attr  = node ? { ...node.attrs } : {}
    openedImage.isNew = !node

    getEditorLink();
    console.log(openedImage);
    return openedImage
  }


  function getEditorLink() {
    resetOpenedLink()
    let protocol = ''
    openedLink.type = 'URL'

    if (editor.value.isActive('link')) {
      const attrs = { ...editor.value.getAttributes('link') }
      setOpenedLinkProps(attrs)
      openedLink.attr = { ...attrs }
      openedLink.isNew = false
    }

    const { selection } = editor.value.state
    openedLink.text = editor.value.state.doc.textBetween(selection.from, selection.to, ' ')
    openedLink.protocol ||= protocol
    openedLink.setUp = true
    return openedLink
  }

  // Template Injector integration
  function getInjection(ev) {
    if (!hookedOpenItem?.value) {
      return;
    }
    const image_data2 = {}
    for (let i = 0, args; (args = ev.detail[i]); i++) {
      const [, name, value] = args
      image_data2[name] = value
      if (name === 'target_node_id') {
        hookedOpenItem.value.attr['data-node-id'] = value
      } else {
        hookedOpenItem.value.attr[name] = value
      }
      if (name === 'width' || name === 'height') {
        hookedOpenItem.value[name] = value
      }
    }
    if (image_data2.href) {
      setOpenedLinkProps(image_data2)
    }
  }

  const hookedOpenItem = ref(null) // either openedImage or openedLink

  function addEventListener() {
    window.addEventListener('i:templateInjector', getInjection)
  }
  function removeEventListener() {
    window.removeEventListener('i:templateInjector', getInjection)
    hookedOpenItem.value = null
  }

  function callInsertionWindow(toHook, inject_type = '/document/media') {
    if (!window.I?.Util || !window.I?.template || !toHook) return

    const insert_params = { __imperia_node_id: 'target_node_id', PREVURL: 'preview', NAME: 'alt' }
    const callOpts = { name: 'tiptapInject' }

    if (toHook === 'link') {
      hookedOpenItem.value = openedLink
      insert_params.OBJURL = 'href'
      insert_params.title = 'title'
    } else {
      hookedOpenItem.value = openedImage
      insert_params.OBJURL = 'src'
      insert_params.width = 'width'
      insert_params.height = 'height'
      insert_params.title = 'title'
    }

    if (inject_type === '/document/media') callOpts.windowName = 'MAMtiptap'
    else if (inject_type === '/document/browser') callOpts.windowName = 'DOCBRtiptap'

    const tmpl = window.templateWindow?.I?.template ?? window.I.template
    Object.assign(callOpts, {
      url: window.I.Util.uri(inject_type, 'fromTemplate=1'),
      params: insert_params,
      code: { function: 'ACProcessValues' },
      context: window,
    })
    tmpl.MAMCall(callOpts)
  }

  function getResizedInjection(MAMwidth, MAMheight, MAMurl, node_id, plugin = 'FitToSize') {
    if (!window?.I?.template || !node_id) return
    let MAMreturnSize
    if (MAMwidth || MAMheight) {
      MAMreturnSize = (MAMwidth || 0) + 'x' + (MAMheight || 0)
    }
    const tmpl = window.I.template
    const data = tmpl.directMAMCall(
      node_id,
      MAMurl,
      {
        name: 'txtUrl',
        params: { OBJURL: 'txtUrl', NAME: 'txtAlt', RETURNSIZE: MAMreturnSize, XRES: 'hWidth', YRES: 'hHeight' },
        code: { plugin },
        ajaxOptions: {
          async: false,
          success: function (data) {
            if (data.success == 1) {
              for (let it = 0; it < data.result.length; it++) {
                const item = data.result[it]
                if (item[1] == 'txtUrl') {
                  openedImage.attr.src = item[2]
                  break
                }
              }
            } else {
              console.warn('Resize failed: ' + data.message)
            }
          },
          error: function () { },
        },
      },
      window,
    )
  }

  function addImage(closeCb) {
    const ed = editor.value
    if (!ed) return typeof closeCb === 'function' && closeCb()

    const hasSrc          = !!openedImage.attr?.src
    const hasCaption      = !!openedImage.attr?.caption
    const isNewImage      = !!openedImage.isNew
    const hasLink         = !!openedLink?.attr?.href
    const shouldUnsetLink = !!openedLink?.setUp && !hasLink

    const currentType = openedImage.node?.type?.name // 'image' | 'figure' | undefined

    // 1) No src => delete node
    if (!hasSrc) {
      if (typeof openedImage.pos === 'number') {
        ed.chain().focus().setNodeSelection(openedImage.pos).deleteSelection().run()
      } else {
        ed.chain().focus().deleteSelection().run()
      }

      if (shouldUnsetLink) {
        ed.chain().focus().extendMarkRange('link').unsetLink().run()
      }

      if (typeof closeCb === 'function') closeCb()
      return
    }

    // 2) Resize side-effect
    const widthChanged  = openedImage.attr.width  && openedImage.attr.width  != openedImage.width
    const heightChanged = openedImage.attr.height && openedImage.attr.height != openedImage.height
    if (widthChanged || heightChanged) {
      getResizedInjection(
        openedImage.attr.width,
        openedImage.attr.height,
        openedImage.attr.src,
        openedImage.attr['data-node-id'],
        'FitToSize'
      )
    }

    // Helper: reliably select current node so deleteSelection removes it
    const selectCurrentNode = () => {
      if (typeof openedImage.pos === 'number') {
        ed.chain().focus().setNodeSelection(openedImage.pos).run()
      } else {
        ed.chain().focus().selectParentNode().run()
      }
    }

    // --- A) CAPTION PRESENT => ensure FIGURE (replace if needed) ---
    if (hasCaption) {
      const figureAttrs = { ...openedImage.attr }
      if (hasLink) figureAttrs.link = { ...openedLink.attr }
      else delete figureAttrs.link

      if (isNewImage) {
        ed.chain().focus().setFigure(figureAttrs).run()
      } else {
        selectCurrentNode()

        // If you had a link mark on an image, remove it before inserting a figure (figure uses attrs.link)
        if (shouldUnsetLink) {
          ed.chain().focus().extendMarkRange('link').unsetLink().run()
        }

        ed.chain().focus().deleteSelection().setFigure(figureAttrs).run()
      }

      openedImage.isNew = false
      openedLink.isNew = false
      openedLink.setUp = false
      if (typeof closeCb === 'function') closeCb()
      return
    }

    // --- B) NO CAPTION => ensure IMAGE ---
    // If we are editing a FIGURE and caption got removed, replace it with an IMAGE
    if (!isNewImage && currentType === 'figure') {
      // Build image attrs: remove figure-only attrs
      const { caption, link, ...imgAttrs } = openedImage.attr || {}

      selectCurrentNode()
      ed.chain().focus().deleteSelection().setImage(imgAttrs).run()

      // Now apply link as a MARK for images
      if (hasLink) {
        if (typeof openedImage.pos === 'number') {
          ed.chain()
            .focus()
            .setNodeSelection(openedImage.pos)
            .setLink({ ...openedLink.attr })
            .run()
        } else {
          // fallback: try to select the image node near the cursor
          ed.chain().focus().selectParentNode().setLink({ ...openedLink.attr }).run()
        }
      } else if (shouldUnsetLink) {
        ed.chain().focus().extendMarkRange('link').unsetLink().run()
      }

      openedImage.isNew = false
      openedLink.isNew = false
      openedLink.setUp = false
      if (typeof closeCb === 'function') closeCb()
      return
    }

    // Otherwise it's a normal IMAGE update/insert
    if (isNewImage) {
      const chain = ed.chain().focus().setImage({ ...openedImage.attr })

      if (hasLink) {
        const { selection } = ed.state
        chain.setNodeSelection(selection.from).setLink({ ...openedLink.attr }).run()
      } else {
        chain.run()
        if (shouldUnsetLink) ed.chain().focus().extendMarkRange('link').unsetLink().run()
      }
    } else {
      ed.chain().focus().updateAttributes('image', { ...openedImage.attr }).run()

      if (hasLink) {
        ed.chain().focus().extendMarkRange('link').setLink({ ...openedLink.attr }).run()
      } else if (shouldUnsetLink) {
        ed.chain().focus().extendMarkRange('link').unsetLink().run()
      }
    }

    openedImage.isNew = false
    openedLink.isNew = false
    openedLink.setUp = false

    if (typeof closeCb === 'function') closeCb()
  }

  function addLink(closeCb) {
    const href = buildHrefFromState(openedLink)
    openedLink.attr.href = href

    if (!openedLink.isNew && !openedLink.attr.href && !openedLink.attr.name) {
      editor.value.chain().focus().extendMarkRange('link').unsetLink().run()
      if (typeof closeCb === 'function') closeCb()
      return
    }

    if (!openedLink.attr.href) {
      if (openedLink.setUp && !openedLink.attr.name) {
        editor.value.chain().focus().extendMarkRange('link').unsetLink().run()
      }
      if (typeof closeCb === 'function') closeCb()
      return false
    }

    const chain = editor.value.chain().focus()
    if (openedLink.isNew) {
      chain
        .setLink({ ...openedLink.attr })
        .updateAttributes('link', { ...openedLink.attr })
        .run()
    } else {
      chain
        .extendMarkRange('link')
        .setLink({ ...openedLink.attr })
        .updateAttributes('link', { ...openedLink.attr })
        .run()
    }

    if (openedLink.text) {
      editor.value.chain().focus().insertContent(openedLink.text).run()
    }

    const { to } = editor.value.state.selection
    editor.value.chain()
      .setTextSelection(to)
      .unsetMark('link')
      .run();

    if (typeof closeCb === 'function') closeCb()
  }


  // Lifecycle wiring for the external injector
  onMounted(addEventListener)
  onBeforeUnmount(removeEventListener)

  return {
    // state
    DEFAULT_IMAGE_WIDTH,
    openedImage,
    openedLink,

    // options
    linkTargets,
    linkTypes,
    protocols,

    // lingua helpers
    __,
    __x,

    // methods
    activeLabel,
    resetOpenedImage,
    resetOpenedLink,
    setOpenedLinkProps,
    getEditorImage,
    getEditorLink,
    getInjection,
    callInsertionWindow,
    getResizedInjection,
    addImage,
    addLink,
    addEventListener,
    removeEventListener,
  }
}
